import type { ReactNode } from 'react';
import RatePicker, { type IRatePickerProps } from '../../atoms/RatePicker';
import { useHookFormController } from '../../hooks/useHookFormController';
import type { IControlProps, IPartialFormGroup } from '../types';
import FormControl from '../FormControl';
import { getRequiredMessage } from '../../lib/requiredMessage';

/** Фокус формы (`setFocus`, ошибка валидации) — на выбранную звезду, иначе на первую: корень Rating не фокусируется. */
const ratingFocusTarget = (rating: HTMLDivElement) => ({
    focus: () =>
        (
            rating.querySelector<HTMLInputElement>('input[type="radio"]:checked') ??
            rating.querySelector<HTMLInputElement>('input[type="radio"]:not(:disabled)')
        )?.focus(),
});

export type IRatePickerControlProps = Omit<IRatePickerProps, 'defaultValue' | 'name' | 'onChange' | 'value'> &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        onValueChange?: (value: number) => void;
    };

const RatePickerControl = ({
    name,
    rules,
    defaultValue,
    control,
    shouldUnregister,
    formGroupProps,
    label,
    tooltip,
    required,
    onValueChange,
    ...props
}: IRatePickerControlProps) => {
    const Controller = useHookFormController();
    const configuredValidators =
        typeof rules?.validate === 'function'
            ? { configured: rules.validate }
            : rules?.validate && typeof rules.validate === 'object'
              ? rules.validate
              : {};
    const requiredValidationDisabled =
        rules?.required === false ||
        (typeof rules?.required === 'object' && 'value' in rules.required && !rules.required.value);
    const validateRequired = required && !requiredValidationDisabled;
    const requiredMessage = getRequiredMessage(
        'rating',
        typeof rules?.required === 'string'
            ? rules.required
            : typeof rules?.required === 'object'
              ? rules.required.message
              : undefined,
    );
    const ratingRules = {
        ...rules,
        validate: {
            ...configuredValidators,
            ratingRange: (value: unknown) => {
                const rating = Number(value);
                return (
                    value === null ||
                    value === undefined ||
                    value === '' ||
                    (Number.isInteger(rating) && rating >= 0 && rating <= 5) ||
                    'Допустимое значение от 0 до 5'
                );
            },
            ...(validateRequired
                ? {
                      ratingRequired: (value: unknown) =>
                          (Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 5) ||
                          requiredMessage,
                  }
                : {}),
        },
    };

    return (
        <Controller
            control={control}
            defaultValue={defaultValue}
            name={name}
            rules={ratingRules}
            shouldUnregister={shouldUnregister}
            render={({ field }) => (
                <FormControl
                    readonlyAppearance={props.readOnly ? 'hint' : undefined}
                    control={control}
                    label={label}
                    name={name}
                    required={required}
                    tooltip={tooltip}
                    {...formGroupProps}
                >
                    <RatePicker
                        {...props}
                        ref={(rating: HTMLDivElement | null) => field.ref(rating && ratingFocusTarget(rating))}
                        aria-required={required || undefined}
                        name={field.name}
                        onBlur={field.onBlur}
                        value={Number(field.value) || 0}
                        onChange={(nextValue) => {
                            field.onChange(nextValue);
                            onValueChange?.(nextValue);
                        }}
                    />
                </FormControl>
            )}
        />
    );
};

export default RatePickerControl;
